/**
 * Client view of the server-authoritative enemy field. Most kinds render as
 * procedural meshes (dark octa/hex/cone hull, glowing eye, spinning guard ring)
 * easing toward the 10 Hz snapshots; HUMANOID ("player_*") kinds instead render
 * a full character Avatar driven by the SAME state stream via the shared
 * avatarDriver (the path remote players use). The server owns existence + health
 * — this renders what it's told, feeds the bolt collider list, and explodes on
 * death messages.
 */
import * as THREE from "three";
import {
  DRONE_INTERP_BUFFERED,
  DRONE_HIT_MARGIN,
  DRONE_RADIUS,
  DRONE_CLASS,
  isHumanoid,
  HUMANOID_WEAPON,
  DEFAULT_STYLE,
  COMBAT_ACTIONS,
  type CombatActionId,
  type CharacterStyle,
  type DroneKind,
  type DroneState,
  type DroneAnim,
  type DroneMeleeMsg,
  type DroneActMsg,
} from "@hyperlight/shared";

/** One buffered snapshot for the players-style −INTERP_DELAY drone timeline. */
interface DroneSnap {
  t: number; // server ms (DronesMsg.time)
  px: number; py: number; pz: number; // world position
  hasL: boolean; // humanoid local pose present
  lx: number; ly: number; lz: number; // planet-LOCAL position
  /** The POSTURE that shipped with this position (humanoids). Rendered from the SAMPLE, not
   *  from the newest snapshot — see the render-time posture note in driveHumanoid. */
  a?: DroneAnim;
}
/** Ring depth: ~0.5 s of 10 Hz snapshots — plenty around the −150 ms sample point. */
const SNAP_KEEP = 6;
/** Cap on extrapolating past the newest snapshot (matches the remote-player cap). */
const EXTRAP_MAX_MS = 300;
import type { Collider } from "../fx/bolts";
import type { MeleeTarget } from "../combat/meleeContact";
import { Avatar } from "../character/avatar";
import { driveAvatar, type AvatarDriveState } from "../character/avatarDriver";
import type { MoveId } from "../character/moves";
import type { StarSystem } from "./starsystem";

interface DroneView {
  serverHits?: boolean;
  group: THREE.Group;
  kind: string;
  humanoid: boolean;
  /** Humanoid render: the full character model (mesh kinds leave this null). */
  avatar: Avatar | null;
  ring: THREE.Mesh | null;
  eye: THREE.Mesh | null;
  sysPos: THREE.Vector3;
  snapPos: THREE.Vector3;
  snapVel: THREE.Vector3;
  snapAt: number; // performance.now() of the snapshot
  hp: number;
  seen: boolean;
  collider: Collider;
  /** Melee lunge animation (mesh kinds only): seconds left + world strike dir. */
  lungeT: number;
  lungeDir: THREE.Vector3;
  /** Humanoid: latest facing + posture + radial up. `face` is planet-LOCAL when
   *  `hasLocal` (anchored render); otherwise treated as world (fallback). */
  face: THREE.Vector3;
  /** NEWEST streamed posture — the GAMEPLAY read (target lists, the riposte's staggered gate). */
  anim: DroneAnim;
  /** RENDER posture: the one that shipped with the position currently being drawn (the buffered
   *  sample). Falls back to `anim` on the legacy easing path, where position IS the newest. */
  animR: DroneAnim | null;
  up: THREE.Vector3;
  /** Humanoid anchor (RemoteShip-style): interpolate the planet-LOCAL position and
   *  convert to world via the planet's rendered pose, so the spinning world can't
   *  jitter a foot enemy. `pi` = planet index; `hasLocal` once a snapshot carried it. */
  localPos: THREE.Vector3;
  snapLocal: THREE.Vector3;
  pi: number;
  hasLocal: boolean;
  /** Buffered snapshot ring (DRONE_INTERP_BUFFERED): sampled at serverNow − INTERP_DELAY,
   *  exactly like remote players — no extrapolation error on direction changes. */
  snaps: DroneSnap[];
  /** True while `localPos`/`pi` hold a planet-LOCAL anchor captured from THIS frame's rendered
   *  planet pose — the frame the fixed-tick melee sweep resolves in (see `anchor`). */
  anchored: boolean;
  /** DEATH THROES clock (humanoids): 0 = alive; >0 = seconds since the kill — the view plays
   *  the knockdown, fades, and is removed locally (the server already dropped it). */
  dyingT: number;
  /** Small-hit flinch cooldown (s) so full-auto fire doesn't stunlock the reaction. */
  flinchCd: number;
  /** Last bolt travel direction that struck this view (world) — seeds the death ragdoll. */
  lastHitDir: THREE.Vector3;
  /** Stable per-view melee target (like `collider`): live refs into sysPos/localPos, no copies. */
  melee: MeleeTarget;
}

// Flying: octa hull + spinning guard ring + eye (the original silhouette).
const hullGeo = new THREE.OctahedronGeometry(0.85, 0);
const ringGeo = new THREE.TorusGeometry(1.15, 0.09, 6, 18);
const eyeGeo = new THREE.SphereGeometry(0.26, 10, 8);
// Grounded: a squat hex hover-tank body. Melee: a forward wedge (points -Z).
const groundGeo = new THREE.CylinderGeometry(0.95, 1.35, 0.7, 6);
const meleeGeo = (() => {
  const g = new THREE.ConeGeometry(0.85, 1.7, 4);
  g.rotateX(Math.PI / 2); // tip toward -Z (forward)
  return g;
})();

/** Per-kind silhouette: scale + accent so the roster reads at a glance. */
const KIND_LOOK: Record<string, { scale: number; ring: number; eye: number }> = {
  gunner: { scale: 1, ring: 0x4a5563, eye: 0xff4455 },
  scout: { scale: 0.72, ring: 0x3f7f8a, eye: 0x4ad9ff },
  heavy: { scale: 1.65, ring: 0x6a4a3f, eye: 0xff8a33 },
  swarmer: { scale: 0.5, ring: 0x7a6a2f, eye: 0xffd24a },
  sniper: { scale: 0.9, ring: 0x5a4a7a, eye: 0xc06aff },
  skirmisher: { scale: 0.9, ring: 0x3f7a4f, eye: 0x6effa0 },
  trooper: { scale: 1.25, ring: 0x3f6a4a, eye: 0x8effb0 },
  bastion: { scale: 1.9, ring: 0x4a6a3f, eye: 0xbaff7a },
  biter: { scale: 0.7, ring: 0x7a3f3f, eye: 0xff6a4a },
  mauler: { scale: 1.05, ring: 0x7a2f2f, eye: 0xff5533 },
  brute: { scale: 1.7, ring: 0x6a2f2f, eye: 0xff3322 },
};

/** Hostile suit colors so humanoid enemies read as foes, not teammates. */
const ENEMY_STYLE: CharacterStyle = { ...DEFAULT_STYLE, suit: 0x5a1e1e, accent: 0xff3b2f };

// Scratch for humanoid orientation (avoid per-frame allocation).
const _z = new THREE.Vector3();
const _zt = new THREE.Vector3();
const _bhFwd = new THREE.Vector3();
const _bhRight = new THREE.Vector3();
const _rdImp = new THREE.Vector3();
/** Death-throes timing (s): reel duration before the lying loop takes over; fade start + span. */
const DEATH_REEL_S = 1.15;
const DEATH_FADE_AT = 1.8;
const DEATH_FADE_S = 0.9;
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _lw = new THREE.Vector3();
const _fallbackCam = new THREE.Vector3();
const _gpPoint = new THREE.Vector3();
const _gpUp = new THREE.Vector3();
const _unspin = new THREE.Quaternion();
/** Scratch result of sampleSnaps (world + optional local pose at the sampled time). */
const _snapOut = { wx: 0, wy: 0, wz: 0, hasL: false, lx: 0, ly: 0, lz: 0, a: undefined as DroneAnim | undefined };

/** Re-ground a system-space point to the CLIENT's terrain: fills `outPoint`
 *  (surface point) + `outUp` (radial up), returns false if not on a planet. */
export type GroundFn = (sysPos: THREE.Vector3, outPoint: THREE.Vector3, outUp: THREE.Vector3) => boolean;

export class DroneSwarm {
  private views = new Map<number, DroneView>();
  private hullMat: THREE.MeshStandardMaterial;
  private ringMat: THREE.MeshStandardMaterial;
  private eyeMat: THREE.MeshBasicMaterial;
  private t = 0;
  private lodFrame = 0;

  usesServerHits(id: number): boolean { return this.views.get(id)?.serverHits === true; }

  constructor(private scene: THREE.Scene) {
    // Matte clay to match the scene-wide flat direction (the eye stays unlit).
    this.hullMat = new THREE.MeshStandardMaterial({ color: 0x2c333d, metalness: 0, roughness: 1 });
    this.ringMat = new THREE.MeshStandardMaterial({ color: 0x4a5563, metalness: 0, roughness: 1 });
    this.eyeMat = new THREE.MeshBasicMaterial({ color: 0xff4455 });
  }

  /** Apply a 10 Hz snapshot broadcast. `timeMs` = the server stamp (DronesMsg.time) that feeds
   *  the buffered timeline; callers without one (the outfitter drone harness) fall back to the
   *  legacy ease/extrapolate rendering. */
  applySnapshot(list: DroneState[], timeMs?: number): void {
    const now = performance.now();
    for (const v of this.views.values()) v.seen = false;
    for (const d of list) {
      let v = this.views.get(d.id);
      // A snapshot can race a `ddeath` by a tick — a dying body must not resurrect.
      if (v && v.dyingT > 0) { v.seen = true; continue; }
      const fresh = !v;
      if (!v) {
        v = this.create(d.id, d.kind);
        v.sysPos.set(...d.p);
      }
      v.snapPos.set(...d.p);
      v.snapVel.set(...d.v);
      v.snapAt = now;
      v.hp = d.hp;
      v.serverHits = d.serverHits === true;
      v.seen = true;
      // Humanoid posture/facing for the avatar driver.
      if (v.humanoid) {
        if (d.f) v.face.set(...d.f);
        v.anim = d.a ?? {};
        // Planet-LOCAL anchor (no spinning-world jitter): the snapshot's local pose.
        if (d.lp) {
          v.snapLocal.set(...d.lp);
          v.pi = d.pi ?? 0;
          if (fresh || !v.hasLocal) v.localPos.copy(v.snapLocal); // seed; don't ease in from origin
          v.hasLocal = true;
        }
      }
      // Buffered timeline (players-style): stamp the ring with the SERVER time. Out-of-order
      // arrivals (unordered UDP) are dropped upstream, so pushes stay monotonic.
      if (DRONE_INTERP_BUFFERED && timeMs !== undefined) {
        v.snaps.push({
          t: timeMs,
          px: d.p[0], py: d.p[1], pz: d.p[2],
          hasL: !!d.lp,
          lx: d.lp?.[0] ?? 0, ly: d.lp?.[1] ?? 0, lz: d.lp?.[2] ?? 0,
          a: d.a, // the posture that shipped WITH this position (rendered together)
        });
        if (v.snaps.length > SNAP_KEEP) v.snaps.shift();
      }
    }
    // Gone from the stream (dead/respawning elsewhere): remove quietly —
    // ddeath messages handle the explosion for kills we can see. DYING humanoids are
    // EXPECTED to be absent (the server already dropped them) — the fade owns their removal.
    for (const [id, v] of this.views) {
      if (!v.seen && v.dyingT === 0) this.remove(id, v);
    }
  }

  /** A drone died at `p`. MESH kinds: remove now and return the position (the caller plays the
   *  boom). HUMANOIDS (user call 2026-08-02): no boom — the body plays the combat-lab knockdown
   *  (the hurt-heavy reel that ends on the floor, no kip-up), lies there, and FADES away; the
   *  view lives on locally until the fade finishes. Returns null so the caller skips the boom. */
  death(id: number): THREE.Vector3 | null {
    const v = this.views.get(id);
    if (!v) return null;
    if (v.humanoid && v.avatar) {
      if (v.dyingT === 0) {
        v.dyingT = 1e-4;
        v.hp = 0;
        // RAGDOLL (user call 2026-08-02): verlet body flung along the killing bolt. Falls
        // back to the authored knockdown reel if the rig is missing ragdoll bones.
        _rdImp.copy(v.lastHitDir).multiplyScalar(3.4).addScaledVector(v.up, 1.2);
        v.avatar.startRagdoll(_rdImp);
        if (!v.avatar.ragdollActive()) v.avatar.hurtHeavy(false);
      }
      return null;
    }
    const at = v.sysPos.clone();
    this.remove(id, v);
    return at;
  }

  /** A BOLT landed on drone `id` (predicted contact or authoritative confirm) — the instant
   *  victim feedback: white hit flash + a directional flinch (UAL hit_*) on humanoids, a small
   *  knockback nudge on mesh kinds. `dir` = the bolt's travel direction (world). Flinches are
   *  cooldown-gated so full-auto fire reads as impacts, not a stunlock. */
  boltHit(id: number, dir: THREE.Vector3, impact=1): void {
    const v = this.views.get(id);
    // Dead-on-the-server (hp 0, ddeath in flight) and dying bodies give no feedback — a flash
    // on a corpse is exactly the "white hit with no damage" report.
    if (!v || v.dyingT > 0 || v.hp <= 0) return;
    v.lastHitDir.copy(dir).normalize();
    if (v.humanoid && v.avatar) {
      v.avatar.flashHit();
      if (impact>0 && v.flinchCd <= 0 && !v.anim?.lf) { // a launched/juggled body keeps its lift posture
        v.flinchCd = 0.45;
        // Which side did it come from, in the victim's frame? `dir` travels shooter→victim,
        // so the hit comes FROM −dir. face is planet-local when anchored — compare in world
        // via the avatar's group frame instead (cheap and render-true).
        _bhFwd.set(0, 0, -1).applyQuaternion(v.avatar.group.quaternion);
        _bhRight.set(1, 0, 0).applyQuaternion(v.avatar.group.quaternion);
        const f = -dir.dot(_bhFwd);
        const r = -dir.dot(_bhRight);
        const side = Math.abs(f) >= Math.abs(r) ? (f > 0 ? "f" : "b") : (r > 0 ? "r" : "l");
        v.avatar.flinch(side,impact);
      }
    } else {
      // Mesh drone: a brief knockback nudge along the bolt (reuses the lunge offset).
      v.lungeT = 0.18;
      v.lungeDir.copy(dir).normalize().multiplyScalar(0.35*impact);
    }
  }

  /** Play a melee strike: humanoids throw the relayed combo move; mesh kinds
   *  do their generic forward lunge. Damage is applied on foot (relay model). */
  melee(msg: DroneMeleeMsg): void {
    const v = this.views.get(msg.id);
    if (!v) return;
    if (v.humanoid && v.avatar) {
      // Two relay vocabularies, one field: FIST kinds send a MoveId (the Kickbox
      // dh_ set), BLADE kinds (`duelist`) send a COMBAT_ACTIONS id — the player's own
      // sword_light_* swing, so the strike you parry is the strike you saw. Both
      // arrive off the wire, so both paths must tolerate an unknown string: `move()`
      // guards on MOVES and this guards on COMBAT_ACTIONS.
      const id = msg.move ?? "punch_f";
      if (COMBAT_ACTIONS[id as CombatActionId]) v.avatar.playRelayedAction(id as CombatActionId);
      else v.avatar.move(id as MoveId);
    } else {
      v.lungeT = 0.25;
      v.lungeDir.set(...msg.d).normalize();
    }
  }

  /** JUGGLE-HIT flourish: the local client just landed a lift-carrying hit on drone `id` —
   *  snap the LiftAir hit reaction on its avatar (client-side flourish; the authoritative
   *  lift posture keeps streaming underneath). Mesh kinds no-op. */
  juggleHit(id: number): void {
    this.views.get(id)?.avatar?.liftHit();
  }

  /** Harness seam: the humanoid avatar rendering drone `id` (undefined before first snapshot). */
  avatarOf(id: number): Avatar | undefined {
    return this.views.get(id)?.avatar ?? undefined;
  }

  /** A landed hit's victim flash (white emissive pulse) — humanoid views only. */
  hitFlash(id: number): void {
    this.views.get(id)?.avatar?.flashHit();
  }

  /** A one-shot humanoid action (currently dodge-rolls). */
  act(msg: DroneActMsg): void {
    const v = this.views.get(msg.id);
    if (!v?.avatar) return;
    if (msg.a === "evade") v.avatar.evadeRoll(msg.d);
  }

  /** Live collider bounds for the bolt system (rebuilt refs, stable objs). */
  collectColliders(out: Collider[]): void {
    for (const v of this.views.values()) {
      if (v.dyingT > 0) continue; // corpses don't eat bolts
      // EXACTLY the server's hurt sphere (2026-08-02): centered at the STREAMED position, no
      // torso offset — the old +1.0 torso lift reached 2.4 m above the feet while the server's
      // sphere stopped at 1.95, so high hits FLASHED without damage (user report).
      v.collider.pos.copy(v.sysPos);
      out.push(v.collider);
    }
  }

  get count(): number {
    return this.views.size;
  }

  /**
   * The drone's firing origin AS DRAWN, system space (2026-08-17, user: "weapon
   * bolts do not come from the NPC — they come from nearby it"). The dfire
   * message position is the SERVER's sim pose — on a carried world the rendered
   * body is interp-buffered + planet-carried metres away from it, and a humanoid
   * (Revenant) holds a weapon whose muzzle is nowhere near its centre. Prefer
   * the avatar's live muzzle; else the rendered view centre. False = no view
   * (off-range spawn): the caller falls back to the message position.
   */
  fireOriginSys(id: number, origin: THREE.Vector3, out: THREE.Vector3): boolean {
    const v = this.views.get(id);
    if (!v || !v.seen || v.dyingT > 0) return false;
    if (v.humanoid && v.avatar?.muzzleWorld(out)) {
      out.add(origin); // render → system space
      return true;
    }
    out.copy(v.sysPos);
    return true;
  }

  /** HARNESS seam: override a humanoid kind's held weapon (the combat lab arms its duelist
   *  with the blade). Must live HERE because driveAvatar re-asserts the weapon every frame —
   *  a post-update setWeapon just flip-flops the dissolve and the weapon never builds.
   *  null/undefined → the normal HUMANOID_WEAPON table. Dev-only; nothing in-game sets it. */
  weaponOverride: ((kind: string) => string | null | undefined) | null = null;

  /** Hide the swarm while an editor owns the screen (the void). */
  private worldVisible = true;

  setVisible(visible: boolean): void {
    this.worldVisible = visible;
    for (const v of this.views.values()) v.group.visible = visible;
  }

  /** Debug/test view of the live drones. */
  debugList(): { id: number; p: [number, number, number]; hp: number }[] {
    return [...this.views.entries()].map(([id, v]) => ({
      id,
      p: [v.sysPos.x, v.sysPos.y, v.sysPos.z],
      hp: v.hp,
    }));
  }

  /** Dev/harness: the posture actually being RENDERED for `id` (the buffered sample's, not
   *  the newest snapshot's) vs the newest one — the drone-interp suite's lockstep seam. */
  debugAnim(id: number): { render: DroneAnim; newest: DroneAnim } | null {
    const v = this.views.get(id);
    return v ? { render: v.animR ?? v.anim, newest: v.anim } : null;
  }

  /** Dev/harness: the live humanoid avatars (for the drone render smoke). */
  debugAvatars(): Avatar[] {
    const out: Avatar[] = [];
    for (const v of this.views.values()) if (v.avatar) out.push(v.avatar);
    return out;
  }

  /** Sample the buffered ring at server time `tMs` into `_snapOut`. Straddling snapshots
   *  interpolate; before-the-oldest clamps; past-the-newest extrapolates from the last pair,
   *  capped at EXTRAP_MAX_MS. False when the ring is empty (caller uses the legacy path). */
  private sampleSnaps(v: DroneView, tMs: number): boolean {
    const s = v.snaps;
    if (s.length === 0) return false;
    const first = s[0];
    const last = s[s.length - 1];
    let a = first;
    let b = last;
    if (tMs <= first.t) {
      a = b = first; // clamp to the oldest retained
    } else if (tMs >= last.t) {
      // Extrapolate a capped step past the newest from the last pair's velocity.
      const prev = s.length >= 2 ? s[s.length - 2] : last;
      const span = Math.max(1, last.t - prev.t);
      const ahead = Math.min(EXTRAP_MAX_MS, tMs - last.t) / span;
      _snapOut.wx = last.px + (last.px - prev.px) * ahead;
      _snapOut.wy = last.py + (last.py - prev.py) * ahead;
      _snapOut.wz = last.pz + (last.pz - prev.pz) * ahead;
      _snapOut.hasL = last.hasL && prev.hasL;
      _snapOut.lx = last.lx + (last.lx - prev.lx) * ahead;
      _snapOut.ly = last.ly + (last.ly - prev.ly) * ahead;
      _snapOut.lz = last.lz + (last.lz - prev.lz) * ahead;
      _snapOut.a = last.a;
      return true;
    } else {
      for (let i = s.length - 1; i >= 1; i--) {
        if (s[i - 1].t <= tMs && tMs <= s[i].t) {
          a = s[i - 1];
          b = s[i];
          break;
        }
      }
    }
    const span = Math.max(1, b.t - a.t);
    const f = a === b ? 0 : (tMs - a.t) / span;
    _snapOut.wx = a.px + (b.px - a.px) * f;
    _snapOut.wy = a.py + (b.py - a.py) * f;
    _snapOut.wz = a.pz + (b.pz - a.pz) * f;
    _snapOut.hasL = a.hasL && b.hasL;
    _snapOut.lx = a.lx + (b.lx - a.lx) * f;
    _snapOut.ly = a.ly + (b.ly - a.ly) * f;
    _snapOut.lz = a.lz + (b.lz - a.lz) * f;
    // Posture is DISCRETE state, not an interpolable quantity: it is whatever shipped with the
    // sample's leading snapshot (`a` — the one at or before the render instant).
    _snapOut.a = a.a;
    return true;
  }

  /** `renderTimeMs` = the remote-player render instant (serverNow − INTERP_DELAY, from net) —
   *  drives the buffered drone timeline; null/omitted (harness, offline) → legacy easing. */
  update(dt: number, origin: THREE.Vector3, camPos?: THREE.Vector3, system?: StarSystem | null, ground?: GroundFn, renderTimeMs?: number | null): void {
    this.t += dt;
    this.lodFrame++;
    const now = performance.now();
    const cam = camPos ?? _fallbackCam;
    const rt = DRONE_INTERP_BUFFERED && renderTimeMs != null ? renderTimeMs : null;
    for (const v of this.views.values()) {
      if (v.flinchCd > 0) v.flinchCd -= dt;
      // Humanoids move in the planet-LOCAL frame (driveHumanoid) so the rotating
      // world can't jitter them; mesh drones move in world space below.
      if (v.humanoid && v.avatar) {
        // DEATH THROES: the knockdown reel plays over the (frozen) last streamed pose, holds
        // the floor, then the body pixel-fades and the view is dropped. driveHumanoid keeps
        // running so the corpse stays planet-anchored (the world spins on beneath it).
        if (v.dyingT > 0) {
          v.dyingT += dt;
          v.avatar.setDeathFade(THREE.MathUtils.clamp((v.dyingT - DEATH_FADE_AT) / DEATH_FADE_S, 0, 1));
          if (v.dyingT > DEATH_FADE_AT + DEATH_FADE_S + 0.1) {
            this.remove(v.collider.id, v);
            continue;
          }
          if (v.avatar.ragdollActive()) {
            // Pose-only drive keeps the group planet-anchored (the carry frame the ragdoll
            // sims inside); the verlet body owns every bone.
            this.driveHumanoid(v, dt, origin, cam, system ?? null, ground, rt, true);
            v.avatar.stepRagdoll(dt);
            continue;
          }
        }
        this.driveHumanoid(v, dt, origin, cam, system ?? null, ground, rt);
        continue;
      }

      if (rt !== null && this.sampleSnaps(v, rt)) {
        // Buffered players-style timeline: the sampled position IS smooth — no easing.
        v.sysPos.set(_snapOut.wx, _snapOut.wy, _snapOut.wz);
      } else {
        const age = Math.min(0.4, (now - v.snapAt) / 1000);
        const k = 1 - Math.exp(-10 * dt);
        v.sysPos.x += (v.snapPos.x + v.snapVel.x * age - v.sysPos.x) * k;
        v.sysPos.y += (v.snapPos.y + v.snapVel.y * age - v.sysPos.y) * k;
        v.sysPos.z += (v.snapPos.z + v.snapVel.z * age - v.sysPos.z) * k;
      }
      // Mesh kinds track in WORLD space (unlike humanoids, they stream no `lp`) — so derive the
      // planet-LOCAL anchor here, from the world position we just computed and the pose it was
      // computed against. See `anchor`.
      this.anchor(v, origin, system ?? null);
      v.group.position.copy(v.sysPos).sub(origin);
      // Melee lunge: a quick forward jab that decays over ~0.25s.
      if (v.lungeT > 0) {
        v.lungeT = Math.max(0, v.lungeT - dt);
        const amp = Math.sin((v.lungeT / 0.25) * Math.PI) * 1.4; // 0→peak→0
        v.group.position.addScaledVector(v.lungeDir, amp);
      }
      if (v.ring?.visible) {
        v.ring.rotation.y = this.t * 2.2;
        v.ring.rotation.x = Math.sin(this.t * 1.1) * 0.3;
      }
      if (v.eye) {
        const pulse = 0.75 + Math.sin(this.t * 6) * 0.25;
        (v.eye.material as THREE.MeshBasicMaterial).color.setRGB(1, 0.25 * pulse, 0.3 * pulse);
      }
    }
  }

  /**
   * Capture a MESH drone's planet-LOCAL anchor — the frame the fixed-tick melee sweep resolves in.
   *
   * Humanoids get theirs for free: the server streams `lp`/`pi` and driveHumanoid POSES them from it
   * (world = local × the planet's rendered transform). Mesh kinds have no `lp` on the wire and are
   * tracked in raw world space, so we invert that same rendered transform to recover where they
   * stand on the planet. Two properties this must have, and the pose choice is what buys both:
   *
   *   - it is the SAME planet pose the drone was DRAWN with this frame (`planet.group`, posed by
   *     system.update just above us), so the anchor reproduces exactly the position on screen. Hit
   *     detection has to agree with what the player SEES, not with some truer position they never
   *     saw — the buffered timeline renders mesh drones at serverNow − INTERP_DELAY, and this
   *     inherits that lag rather than silently correcting for it;
   *   - it is a LOCAL coordinate, so it does not age. That is the whole point: the melee tick may
   *     read it several ticks after this frame drew it, and the planet's 62–137 m/s carry over that
   *     gap is common to the player's frame too, and cancels.
   */
  private anchor(v: DroneView, origin: THREE.Vector3, system: StarSystem | null): void {
    const near = system?.nearestPlanet(v.sysPos);
    if (!near) {
      v.anchored = false;
      v.melee.lp = null;
      v.melee.pi = -1;
      return;
    }
    const g = near.planet.group;
    _unspin.copy(g.quaternion).invert();
    v.localPos.copy(v.sysPos).sub(origin).sub(g.position).applyQuaternion(_unspin);
    v.pi = system!.planets.indexOf(near.planet);
    v.anchored = true;
    v.melee.lp = v.localPos;
    v.melee.pi = v.pi;
  }

  /** Live melee targets (the fixed-tick swept blade). Stable objects, live vector refs — no
   *  allocation, like collectColliders. `out` is cleared first. */
  collectMeleeTargets(out: MeleeTarget[]): MeleeTarget[] {
    out.length = 0;
    for (const v of this.views.values()) {
      if (v.hp > 0 && v.group.visible) out.push(v.melee);
    }
    return out;
  }

  /** Orient + drive a humanoid enemy's avatar from its streamed posture. */
  private driveHumanoid(v: DroneView, dt: number, origin: THREE.Vector3, cam: THREE.Vector3, system: StarSystem | null, ground?: GroundFn, rt: number | null = null, poseOnly = false): void {
    const avatar = v.avatar!;
    const planet = v.hasLocal && system && v.pi >= 0 ? system.planets[v.pi] : null;
    if (planet) {
      // ANCHORED PATH (real game): position in the planet's LOCAL frame — time-invariant for
      // a standing target — then convert to world via the planet's RENDERED transform
      // (group.quaternion/position), exactly like RemoteShip. Buffered timeline when available
      // (players-style sampling — smooth by construction); legacy chase-lerp otherwise.
      if (rt !== null && this.sampleSnaps(v, rt) && _snapOut.hasL) {
        v.localPos.set(_snapOut.lx, _snapOut.ly, _snapOut.lz);
        // POSTURE IN LOCKSTEP WITH THE POSITION (user report 2026-07-24: "the knockdown does
        // not hit the ground — the body floats"). The buffered timeline renders the body at
        // serverNow − INTERP_DELAY, but the posture used to come from the NEWEST snapshot, so
        // every lift transition fired a full interp delay EARLY: a body falling at up to
        // FALL_TERM (13 m/s) played its floor-impact ~1.5–2 m up, hung there in the lying
        // pose, then slid down. Render the posture that shipped WITH the sampled position.
        // (`v.anim` stays the NEWEST for gameplay reads — target lists, the riposte gate.)
        if (_snapOut.a) v.animR = _snapOut.a;
      } else {
        // BALLISTIC FAST-TRACK: a launched body (lf up/fall) moves ~10 m/s — the normal
        // chase rate trails it 1–2 m, so it visibly "landed" above the ground and popped.
        const ballistic = v.anim.lf === "up" || v.anim.lf === "fall";
        const lk = 1 - Math.exp(-(ballistic ? 26 : 12) * dt);
        v.localPos.lerp(v.snapLocal, lk);
        v.animR = null; // chasing the NEWEST position → the newest posture is the matching one
      }
      _lw.copy(v.localPos).applyQuaternion(planet.group.quaternion).add(planet.group.position); // render space
      avatar.group.position.copy(_lw);
      v.sysPos.copy(_lw).add(origin); // system space for hit detection / scans
      // Already anchored BY CONSTRUCTION: `localPos` IS the planet-local pose the world position
      // above was built from, so melee resolves against it exactly (see `anchor`).
      v.anchored = true;
      v.melee.lp = v.localPos;
      v.melee.pi = v.pi;
      v.up.copy(v.localPos).normalize().applyQuaternion(planet.group.quaternion);
      // Facing is local too → rotate into world by the same rendered pose.
      _z.copy(v.face).applyQuaternion(planet.group.quaternion);
      if (_z.lengthSq() < 1e-6) _z.set(0, 0, -1); // degenerate → forward = world −Z (matches fallback)
      _z.multiplyScalar(-1).normalize();
    } else {
      // FALLBACK (harness / no planet): world-space ease, optionally re-grounded to a
      // supplied terrain fn. Here `face` is treated as world space.
      const now = performance.now();
      const age = Math.min(0.4, (now - v.snapAt) / 1000);
      // Ballistic fast-track — same reasoning as the anchored path above.
      const ballistic = v.anim.lf === "up" || v.anim.lf === "fall";
      v.animR = null; // world-space chase of the NEWEST snapshot → render its posture
      const k = 1 - Math.exp(-(ballistic ? 24 : 10) * dt);
      v.sysPos.x += (v.snapPos.x + v.snapVel.x * age - v.sysPos.x) * k;
      v.sysPos.y += (v.snapPos.y + v.snapVel.y * age - v.sysPos.y) * k;
      v.sysPos.z += (v.snapPos.z + v.snapVel.z * age - v.sysPos.z) * k;
      const airborne = !!v.anim.air;
      if (!airborne && ground && ground(v.sysPos, _gpPoint, _gpUp)) {
        v.sysPos.copy(_gpPoint);
        v.up.copy(_gpUp);
      } else {
        const np = system?.nearestPlanet(v.sysPos);
        if (np) v.up.copy(v.sysPos).sub(np.planet.pose.center).normalize();
        else if (v.up.lengthSq() < 1e-6) v.up.set(0, 1, 0);
      }
      avatar.group.position.copy(v.sysPos).sub(origin);
      if (v.face.lengthSq() < 1e-6) v.face.set(0, 0, -1);
      _z.copy(v.face).multiplyScalar(-1).normalize();
      // No anchored pose here (harness / no planet): don't clobber `localPos` — the anchored path
      // seeds it from the snapshot and would ease in from a garbage value. Melee falls back to
      // system space, which is exactly right when nothing is carrying either body.
      v.anchored = false;
      v.melee.lp = null;
      v.melee.pi = -1;
    }

    // Build an orientation that maps the avatar's −Z onto its facing and +Y onto up.
    _x.crossVectors(v.up, _z);
    if (_x.lengthSq() < 1e-6) _x.crossVectors(v.up, _zt.set(1, 0, 0)); // degenerate guard (don't clobber _z)
    _x.normalize();
    _y.crossVectors(_z, _x);
    _m.makeBasis(_x, _y, _z);

    avatar.group.quaternion.setFromRotationMatrix(_m);
    avatar.group.visible = this.worldVisible;

    // POSE-ONLY (dying ragdoll): the group anchoring/orientation above is all the caller
    // wants — the verlet body owns the bones, so no drive state may touch them.
    if (poseOnly) return;
    // RENDER posture (see the lockstep note above): the buffered path fills `animR` with the
    // posture that shipped alongside the position being drawn; the legacy path leaves it null
    // and the newest IS the drawn one.
    const a = v.animR ?? v.anim;
    const st: AvatarDriveState = {
      weaponId: this.weaponOverride?.(v.kind) ?? HUMANOID_WEAPON[v.kind] ?? null,
      jet: a.j === 1 ? "hover" : a.j === 2 ? "glide" : "none",
      aiming: a.am ?? false,
      combat: true,
      airborne: a.air ?? false,
      crouch: a.cr ?? false,
      blocking: a.bl ?? false,
      moveFwd: a.mf ?? 0,
      moveRight: a.mr ?? 0,
      climb: a.cl ?? 0,
      pitch: a.pt ?? 0,
      groundSpeed: a.sp ?? v.snapVel.length(),
      lift: a.lf ?? "",
      stagger: a.st ?? "",
    };
    // DEATH THROES posture: once the knockdown reel has landed the body, hold the lying
    // "down" loop (the lab corpse pose) — without this the strike expires and the idle
    // stands the corpse back up mid-fade. Movement zeroes: corpses don't strafe.
    if (v.dyingT > 0) {
      st.moveFwd = 0; st.moveRight = 0; st.groundSpeed = 0; st.aiming = false;
      if (v.dyingT > DEATH_REEL_S) st.lift = "down";
    }
    driveAvatar(avatar, st, dt, cam, this.lodFrame);
  }

  /** Live drones for the ground scan: position + kind (+ staggered — the riposte's
   *  target gate), alive only. */
  list(): { id: number; pos: THREE.Vector3; kind: string; staggered?: boolean }[] {
    const out: { id: number; pos: THREE.Vector3; kind: string; staggered?: boolean }[] = [];
    for (const [id, v] of this.views) {
      if (v.hp > 0 && v.group.visible) {
        out.push({ id, pos: v.sysPos, kind: v.kind, ...(v.anim?.st ? { staggered: true } : {}) });
      }
    }
    return out;
  }

  private create(id: number, kind = "gunner"): DroneView {
    if (isHumanoid(kind as DroneKind)) return this.createHumanoid(id, kind);
    const look = KIND_LOOK[kind] ?? KIND_LOOK.gunner;
    const cls = DRONE_CLASS[kind as keyof typeof DRONE_CLASS] ?? "flying";
    const group = new THREE.Group();
    let ring: THREE.Mesh;
    if (cls === "flying") {
      const hull = new THREE.Mesh(hullGeo, this.hullMat);
      ring = new THREE.Mesh(ringGeo, this.ringMat.clone());
      (ring.material as THREE.MeshStandardMaterial).color.setHex(look.ring);
      ring.rotation.x = Math.PI / 2;
      group.add(hull, ring);
    } else {
      const body = new THREE.Mesh(cls === "melee" ? meleeGeo : groundGeo, this.hullMat);
      ring = new THREE.Mesh(ringGeo, this.ringMat.clone()); // kept off-screen; unifies the view shape
      ring.visible = false;
      group.add(body, ring);
    }
    const eye = new THREE.Mesh(eyeGeo, this.eyeMat.clone());
    (eye.material as THREE.MeshBasicMaterial).color.setHex(look.eye);
    eye.position.set(0, 0, -0.7);
    group.add(eye);
    // Hull/body/ring cast + receive sun shadows; the emissive eye (Basic) doesn't.
    group.traverse((o) => {
      if (o instanceof THREE.Mesh && !(o.material instanceof THREE.MeshBasicMaterial)) {
        o.castShadow = true;
        o.receiveShadow = true;
      }
    });
    group.scale.setScalar(look.scale);
    group.visible = this.worldVisible;
    this.scene.add(group);
    // The SERVER's sphere, exactly — it ignores the visual scale, so the prediction must too.
    const view = this.makeView(id, kind, group, false, null, ring, eye, DRONE_RADIUS + DRONE_HIT_MARGIN);
    this.views.set(id, view);
    return view;
  }

  /** A humanoid enemy: a full character Avatar instead of a procedural mesh. */
  private createHumanoid(id: number, kind: string): DroneView {
    const avatar = new Avatar(this.scene); // ctor adds avatar.group to the scene
    avatar.setStyle(ENEMY_STYLE);
    avatar.setWeapon(HUMANOID_WEAPON[kind] ?? null);
    avatar.group.visible = this.worldVisible;
    const view = this.makeView(id, kind, avatar.group, true, avatar, null, null, DRONE_RADIUS + DRONE_HIT_MARGIN);
    this.views.set(id, view);
    return view;
  }

  private makeView(
    id: number,
    kind: string,
    group: THREE.Group,
    humanoid: boolean,
    avatar: Avatar | null,
    ring: THREE.Mesh | null,
    eye: THREE.Mesh | null,
    colliderRadius: number,
  ): DroneView {
    // The melee target below holds LIVE REFS to these two (never copies), so build them first.
    const sysPos = new THREE.Vector3();
    return {
      kind,
      humanoid,
      avatar,
      group,
      ring,
      eye,
      sysPos,
      snapPos: new THREE.Vector3(),
      snapVel: new THREE.Vector3(),
      snapAt: performance.now(),
      hp: 0,
      seen: true,
      collider: { kind: "drone", id, pos: new THREE.Vector3(), radius: colliderRadius },
      lungeT: 0,
      lungeDir: new THREE.Vector3(),
      face: new THREE.Vector3(0, 0, -1),
      anim: {},
      animR: null,
      up: new THREE.Vector3(0, 1, 0),
      localPos: new THREE.Vector3(),
      snapLocal: new THREE.Vector3(),
      pi: -1,
      hasLocal: false,
      snaps: [],
      anchored: false,
      melee: { id, pos: sysPos, lp: null, pi: -1 },
      dyingT: 0,
      flinchCd: 0,
      lastHitDir: new THREE.Vector3(0, 0, -1),
    };
  }

  private remove(id: number, v: DroneView): void {
    if (v.avatar) {
      v.avatar.dispose(this.scene); // removes its group from the scene
    } else {
      this.scene.remove(v.group);
      (v.ring?.material as THREE.Material | undefined)?.dispose();
      (v.eye?.material as THREE.Material | undefined)?.dispose();
    }
    this.views.delete(id);
  }

  dispose(): void {
    for (const [id, v] of this.views) this.remove(id, v);
    this.hullMat.dispose();
    this.ringMat.dispose();
    this.eyeMat.dispose();
  }
}
